import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  Search, MoreHorizontal, CheckCircle2, XCircle, AlertTriangle, Loader2, CalendarCheck,
  BedDouble, Clock, CreditCard, Moon, User, MapPin, Hash, ArrowRight, ArrowRightLeft, LogOut, Hourglass,
} from "lucide-react";
import { bookingsApi, resolveMediaUrl, type BookingListResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const Route = createFileRoute("/admin/bookings")({
  component: BookingsPage,
});

const statusConfig: Record<string, { bg: string; text: string; icon: typeof CheckCircle2; label: string }> = {
  confirmed: { bg: "bg-emerald-50", text: "text-emerald-700", icon: CheckCircle2, label: "Confirmed" },
  pending:   { bg: "bg-amber-50",  text: "text-amber-700",  icon: Clock,         label: "Pending" },
  manager_approval_pending: { bg: "bg-violet-50", text: "text-violet-700", icon: CalendarCheck, label: "Awaiting Approval" },
  checked_in:  { bg: "bg-blue-50",   text: "text-blue-700",   icon: CheckCircle2,  label: "Checked In" },
  checked_out: { bg: "bg-gray-50",  text: "text-gray-700",   icon: CheckCircle2,  label: "Checked Out" },
  cancelled: { bg: "bg-red-50",    text: "text-red-700",    icon: XCircle,       label: "Cancelled" },
  refunded:  { bg: "bg-sky-50",    text: "text-sky-700",    icon: AlertTriangle, label: "Refunded" },
};

const paymentConfig: Record<string, { bg: string; text: string }> = {
  paid:    { bg: "bg-emerald-50", text: "text-emerald-700" },
  pending: { bg: "bg-amber-50",  text: "text-amber-700" },
  failed:  { bg: "bg-red-50",    text: "text-red-700" },
};

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" });
}

function nightsLabel(n: number) {
  return `${n} night${n !== 1 ? "s" : ""}`;
}

function BookingsPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = hasPermission("bookings.manage");
  const canCheckIn = canManage && !isSuperAdmin;

  const [bookings, setBookings] = useState<BookingListResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    setLoading(true);
    bookingsApi
      .cleanupExpiredExtensions()
      .then((cleanup) => {
        if (cleanup.revertedCount > 0) {
          toast.info(`Reverted ${cleanup.revertedCount} expired extension payment(s): ${cleanup.bookingRefs.join(", ")}`);
        }
      })
      .finally(() => {
        bookingsApi.list({ pageSize: 100 })
          .then(res => setBookings(res.data))
          .catch(err => setError(err instanceof Error ? err.message : "Failed to load bookings"))
          .finally(() => setLoading(false));
      });
  }, []);

  const filtered = bookings.filter(b => {
    const matchQ = !q
      || b.bookingRef.toLowerCase().includes(q.toLowerCase())
      || b.guestName?.toLowerCase().includes(q.toLowerCase())
      || b.venueName.toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "all" || b.status.toLowerCase() === filter.toLowerCase();
    return matchQ && matchF;
  });

  const [processingId, setProcessingId] = useState<string | null>(null);

  const approveBooking = async (id: string, ref: string) => {
    setProcessingId(id);
    try {
      const res = await bookingsApi.approve(id);
      setBookings(bs => bs.map(b => b.id === id ? { ...b, status: res.status } : b));
      toast.success(`Booking ${ref} approved successfully.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to approve booking ${ref}`);
    } finally {
      setProcessingId(null);
    }
  };

  const rejectBooking = async (id: string, ref: string) => {
    setProcessingId(id);
    try {
      const res = await bookingsApi.reject(id, "Manager rejected");
      setBookings(bs => bs.map(b => b.id === id ? { ...b, status: res.status, paymentStatus: res.paymentStatus } : b));
      toast.success(`Booking ${ref} rejected. Guest refunded.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to reject booking ${ref}`);
    } finally {
      setProcessingId(null);
    }
  };

  const checkInBooking = async (id: string, ref: string) => {
    setProcessingId(id);
    try {
      const res = await bookingsApi.checkIn(id);
      setBookings(bs => bs.map(b => b.id === id ? { ...b, status: res.newStatus } : b));
      toast.success(`Guest checked in for ${ref}.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to check in booking ${ref}`);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-bold sm:text-3xl">Bookings</h1>
        <p className="text-sm text-muted-foreground">Manage reservations across all properties.</p>
      </div>

      {/* Stats bar */}
      {!loading && bookings.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total", value: bookings.length, color: "text-foreground" },
            { label: "Confirmed", value: bookings.filter(b => b.status.toLowerCase() === "confirmed").length, color: "text-emerald-600" },
            { label: "Pending", value: bookings.filter(b => b.status.toLowerCase() === "pending").length, color: "text-amber-600" },
            { label: "Revenue", value: formatNaira(bookings.filter(b => b.status.toLowerCase() === "confirmed").reduce((s, b) => s + b.totalAmount, 0)), color: "text-primary" },
          ].map(s => (
            <div key={s.label} className="rounded-xl border border-border/60 bg-card p-3 shadow-soft">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</p>
              <p className={`text-lg font-bold mt-0.5 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-border/60 bg-card shadow-soft overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-border/60 p-4 sm:flex-row sm:items-center sm:justify-between bg-muted/20">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref, guest, venue…" className="pl-9 rounded-full bg-background border-border/60" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {([
              { key: "all", label: "All", icon: CalendarCheck },
              { key: "manager_approval_pending", label: "Needs Approval", icon: Hourglass },
              { key: "confirmed", label: "Confirmed", icon: CheckCircle2 },
              { key: "checked_in", label: "Checked In", icon: ArrowRight },
              { key: "checked_out", label: "Checked Out", icon: LogOut },
              { key: "pending", label: "Pending", icon: Clock },
              { key: "cancelled", label: "Cancelled", icon: XCircle },
            ] as const).map(({ key, label, icon: Icon }) => (
              <Button key={key} size="sm" variant={filter === key ? "default" : "outline"} onClick={() => setFilter(key)} className="rounded-full">
                <Icon className="mr-1 size-3.5" /> {label}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading bookings…
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-destructive">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <CalendarCheck className="mb-3 size-10 text-muted-foreground/30" />
            <p className="text-sm font-semibold text-muted-foreground">
              {bookings.length === 0 ? "No bookings yet" : "No bookings match your filters"}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3 sm:hidden">
              {filtered.map((b) => {
                const sConf = statusConfig[b.status.toLowerCase()] ?? statusConfig.pending;
                const SIcon = sConf.icon;
                const pConf = paymentConfig[b.paymentStatus?.toLowerCase() ?? "pending"] ?? { bg: "bg-muted", text: "text-muted-foreground" };
                return (
                  <div key={b.id} className="rounded-2xl border border-border/60 bg-background p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        {b.venueId ? (
                          <Link to="/venues/$venueId" params={{ venueId: b.venueId }} className="block truncate text-sm font-semibold text-foreground hover:text-primary hover:underline">{b.venueName}</Link>
                        ) : (
                          <p className="text-sm font-semibold text-foreground truncate">{b.venueName}</p>
                        )}
                        <p className="text-xs text-muted-foreground truncate">{b.guestName}</p>
                        <p className="mt-2 text-[11px] font-mono text-muted-foreground truncate">{b.bookingRef}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`rounded-full px-2.5 py-1.5 text-[10px] font-semibold ${sConf.bg} ${sConf.text}`}>
                          <SIcon className="size-3" /> {sConf.label}
                        </span>
                        {b.isExtended && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-1 text-[10px] font-semibold text-violet-700">
                            <ArrowRightLeft className="size-2.5" /> Extended
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 grid gap-2 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-xl bg-muted/70 p-3">
                          <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Dates</p>
                          <p className="mt-1 font-semibold">{fmtDate(b.startDate)}</p>
                          <p className="text-[11px] text-muted-foreground">to {fmtDate(b.endDate)}</p>
                        </div>
                        <div className="rounded-xl bg-muted/70 p-3">
                          <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Nights</p>
                          <p className="mt-1 font-semibold">{b.daysCount}</p>
                          <p className="text-[11px] text-muted-foreground">{nightsLabel(b.daysCount)}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-xl bg-muted/70 p-3">
                          <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Amount</p>
                          <p className="mt-1 font-semibold">{formatNaira(b.totalAmount)}</p>
                        </div>
                        <div className="rounded-xl bg-muted/70 p-3">
                          <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Payment</p>
                          <p className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] font-medium capitalize ${pConf.bg} ${pConf.text}`}>{b.paymentStatus || "Pending"}</p>
                        </div>
                      </div>
                    </div>
                    {canManage && (
                      <div className="mt-3 flex justify-end">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="rounded-full">Actions</Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {b.status === "manager_approval_pending" && (
                              <>
                                <DropdownMenuItem disabled={processingId === b.id} onClick={() => approveBooking(b.id, b.bookingRef)}><CheckCircle2 className="mr-2 size-4 text-emerald-600" />{processingId === b.id ? "Approving..." : "Approve"}</DropdownMenuItem>
                                <DropdownMenuItem disabled={processingId === b.id} onClick={() => rejectBooking(b.id, b.bookingRef)} className="text-red-600"><XCircle className="mr-2 size-4" />{processingId === b.id ? "Rejecting..." : "Reject"}</DropdownMenuItem>
                              </>
                            )}
                            {b.status === "confirmed" && (
                              <>
                                {canCheckIn && (
                                  <DropdownMenuItem disabled={processingId === b.id} onClick={() => checkInBooking(b.id, b.bookingRef)}><CalendarCheck className="mr-2 size-4 text-blue-600" />{processingId === b.id ? "Checking in…" : "Check In Guest"}</DropdownMenuItem>
                                )}
                                <DropdownMenuItem disabled><CheckCircle2 className="mr-2 size-4 text-emerald-600" />Confirmed</DropdownMenuItem>
                              </>
                            )}
                            {b.status === "checked_in" && (
                              <>
                                <DropdownMenuItem disabled><CalendarCheck className="mr-2 size-4 text-blue-600" />Checked In</DropdownMenuItem>
                                <DropdownMenuItem disabled><CheckCircle2 className="mr-2 size-4 text-emerald-600" />Active Stay</DropdownMenuItem>
                              </>
                            )}
                            {b.status === "cancelled" && (
                              <DropdownMenuItem disabled><XCircle className="mr-2 size-4 text-red-600" />Cancelled</DropdownMenuItem>
                            )}
                            {b.status === "pending" && (
                              <DropdownMenuItem disabled><Clock className="mr-2 size-4 text-amber-600" />Awaiting Payment</DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-semibold sm:px-5 w-[300px]">Property & Guest</th>
                    <th className="hidden px-4 py-3 font-semibold md:table-cell sm:px-5">Dates</th>
                    <th className="hidden px-4 py-3 font-semibold sm:table-cell sm:px-5">Nights</th>
                    <th className="px-4 py-3 font-semibold sm:px-5">Amount</th>
                    <th className="px-4 py-3 font-semibold sm:px-5">Status</th>
                    <th className="hidden px-4 py-3 font-semibold sm:table-cell sm:px-5">Payment</th>
                    {canManage && <th className="px-4 py-3 sm:px-5 w-10"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {filtered.map((b) => {
                    const sConf = statusConfig[b.status.toLowerCase()] ?? statusConfig.pending;
                    const SIcon = sConf.icon;
                    const pConf = paymentConfig[b.paymentStatus?.toLowerCase() ?? "pending"] ?? { bg: "bg-muted", text: "text-muted-foreground" };
                    return (
                      <tr key={b.id} className="group hover:bg-muted/20 transition-colors">
                        {/* Property & Guest */}
                        <td className="px-4 py-4 sm:px-5">
                          <div className="flex items-center gap-3">
                            {/* Venue Image */}
                            <Link to="/venues/$venueId" params={{ venueId: b.venueId }} className="relative shrink-0" title="View listing on website">
                              {b.venueImage ? (
                                <img
                                  src={resolveMediaUrl(b.venueImage)}
                                  alt={b.venueName}
                                  className="size-12 rounded-xl object-cover ring-1 ring-border/60 shadow-sm"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="size-12 rounded-xl bg-muted flex items-center justify-center ring-1 ring-border/60">
                                  <BedDouble className="size-5 text-muted-foreground/40" />
                                </div>
                              )}
                              <span className={`absolute -bottom-1 -right-1 rounded-full border-2 border-white p-0.5 ${sConf.bg}`}>
                                <SIcon className={`size-2.5 ${sConf.text}`} />
                              </span>
                            </Link>
                            <div className="min-w-0">
                              <Link to="/venues/$venueId" params={{ venueId: b.venueId }} className="block truncate text-sm font-semibold hover:text-primary hover:underline">{b.venueName}</Link>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <User className="size-3 text-muted-foreground/60" />
                                <span className="text-xs text-muted-foreground truncate">{b.guestName}</span>
                              </div>
                              {b.roomTypeName && (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <BedDouble className="size-3 text-muted-foreground/60" />
                                  <span className="text-[11px] text-muted-foreground/70">{b.roomTypeName}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1 mt-1 lg:hidden">
                                <Hash className="size-2.5 text-muted-foreground/50" />
                                <span className="text-[10px] font-mono text-muted-foreground/50">{b.bookingRef}</span>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Dates */}
                        <td className="hidden px-4 py-4 md:table-cell sm:px-5">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium">{fmtDate(b.startDate)}</span>
                            <ArrowRight className="size-3 text-muted-foreground/40" />
                            <span className="font-medium">{fmtDate(b.endDate)}</span>
                          </div>
                          <div className="flex items-center gap-1 mt-1 text-[11px] text-muted-foreground">
                            <Clock className="size-3" />
                            Booked {new Date(b.createdAt).toLocaleDateString("en-NG", { day: "2-digit", month: "short" })}
                          </div>
                          {b.isExtended && (
                            <div className="mt-1">
                              <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700">
                                <ArrowRightLeft className="size-2.5" /> Extended
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Nights */}
                        <td className="hidden px-4 py-4 sm:table-cell sm:px-5">
                          <div className="flex items-center gap-1.5">
                            <Moon className="size-3.5 text-muted-foreground/50" />
                            <span className="text-sm font-medium">{b.daysCount}</span>
                          </div>
                          <span className="text-[11px] text-muted-foreground">{nightsLabel(b.daysCount)}</span>
                        </td>

                        {/* Amount */}
                        <td className="px-4 py-4 sm:px-5">
                          <span className="font-bold text-sm">{formatNaira(b.totalAmount)}</span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-4 sm:px-5">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold ${sConf.bg} ${sConf.text}`}>
                            <SIcon className="size-3" />
                            {sConf.label}
                          </span>
                        </td>

                        {/* Payment */}
                        <td className="hidden px-4 py-4 sm:table-cell sm:px-5">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium capitalize ${pConf.bg} ${pConf.text}`}>
                            <CreditCard className="size-3" />
                            {b.paymentStatus || "Pending"}
                          </span>
                        </td>

                        {/* Actions */}
                        {canManage && (
                          <td className="px-4 py-4 sm:px-5">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="ghost" className="size-8 opacity-0 group-hover:opacity-100 transition-opacity"><MoreHorizontal className="size-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {b.status === "manager_approval_pending" && (
                                  <>
                                    <DropdownMenuItem
                                      disabled={processingId === b.id}
                                      onClick={() => approveBooking(b.id, b.bookingRef)}
                                    >
                                      <CheckCircle2 className="mr-2 size-4 text-emerald-600" />
                                      {processingId === b.id ? "Approving..." : "Approve"}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      disabled={processingId === b.id}
                                      onClick={() => rejectBooking(b.id, b.bookingRef)}
                                      className="text-red-600"
                                    >
                                      <XCircle className="mr-2 size-4" />
                                      {processingId === b.id ? "Rejecting..." : "Reject"}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {b.status === "confirmed" && (
                                  <>
                                    {canCheckIn && (
                                      <DropdownMenuItem disabled={processingId === b.id} onClick={() => checkInBooking(b.id, b.bookingRef)}><CalendarCheck className="mr-2 size-4 text-blue-600" />{processingId === b.id ? "Checking in…" : "Check In Guest"}</DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem disabled><CheckCircle2 className="mr-2 size-4 text-emerald-600" />Confirmed</DropdownMenuItem>
                                  </>
                                )}
                                {b.status === "checked_in" && (
                                  <>
                                    <DropdownMenuItem disabled><CalendarCheck className="mr-2 size-4 text-blue-600" />Checked In</DropdownMenuItem>
                                    <DropdownMenuItem disabled><CheckCircle2 className="mr-2 size-4 text-emerald-600" />Active Stay</DropdownMenuItem>
                                  </>
                                )}
                                {b.status === "cancelled" && (
                                  <DropdownMenuItem disabled><XCircle className="mr-2 size-4 text-red-600" />Cancelled</DropdownMenuItem>
                                )}
                                {b.status === "pending" && (
                                  <DropdownMenuItem disabled><Clock className="mr-2 size-4 text-amber-600" />Awaiting Payment</DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
